import React, { useRef, useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from "chart.js";
import { Bar } from "react-chartjs-2";

import ColorIndexManager from "utils/ColorIndexManager";
import ANChartJSWrapper from "elements/ANChartJSWrapper";
import CustomChartTooltip from "../CustomChartTooltip";
import { colors, FILTERS } from "../utils";
import { getNativeOptionsChart } from "../getNativeOptionsChart";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const colorManager = new ColorIndexManager(colors.length);

const optionsBarChart = getNativeOptionsChart({
  barMaxBarThickness: {
    sm: 28,
    lg: 58,
  },
  barBorderRadius: {
    sm: 5,
    lg: 8,
  },
  barBorderWidth: {
    sm: 2.2,
    lg: 2.2,
  },
});

/**
 * @component
 * @description `DeliveredCharts` component to display delivered hours by client and time unit
 * (weeks or months) in a bar chart.
 * @param {Object} props
 * @param {{
 *   code: string,
 *   date: string,
 *   hours: number[]
 * }[]} props.data - The data to display in the chart
 * @param {string[]} props.labels - The labels to display in the chart
 * @param {string} props.period - The time unit to filter the data by
 * @returns {JSX.Element} Delivered hours chart by client and time unit
 */
const DeliveredCharts = ({ data, labels, period }) => {
  const [chartContext, setChartContext] = useState(null);
  const chartRef = useRef(null);

  const allDatasets = data.map(({ code, dates, hours, totalDuration }) => ({
    label: code,
    data: hours,
    backgroundColor: colors[colorManager.get(code)],
    dates,
    period,
    totalDuration,
  }));

  const periodText = period === FILTERS.BY_WEEKS ? "week" : "month";

  const dataBars = {
    labels: labels,
    datasets: allDatasets,
  };

  const handleChartMouseEnter = () => {
    setChartContext({
      config: chartRef.current?.config,
      tooltip: chartRef.current?.tooltip,
    });
  };

  const handleChartMouseLeave = () => {
    setChartContext(null);
  };

  return (
    <ANChartJSWrapper
      chartContext={chartContext}
      customTooltipContent={
        <CustomChartTooltip
          config={chartContext?.config}
          tooltip={chartContext?.tooltip}
          subTitle={`Delivered this ${periodText}: `}
        />
      }
      customTooltipProps={{ width: "187px" }}
    >
      <Bar
        options={optionsBarChart}
        data={dataBars}
        ref={chartRef}
        onMouseMove={handleChartMouseEnter}
        onMouseLeave={handleChartMouseLeave}
      />
    </ANChartJSWrapper>
  );
};

export default DeliveredCharts;
